// frontend/src/pages/public/legal/LegalLayout.tsx
// ───────────────────────────────────────────────────────────────────
// Layout partagé des pages légales (TASK-054). Public, zéro dépendance auth.
// Pose title + meta description dynamiques (pattern Signup.tsx), un lien retour
// accueil, et un pied de page croisant les 3 pages légales.
// ───────────────────────────────────────────────────────────────────

import { useEffect, type ReactNode, type JSX } from 'react';
import { Link } from 'react-router-dom';

import { LEGAL_LAST_UPDATED, PRODUCT } from '@/lib/legal';

/** Liens croisés entre les pages légales (réutilisé par le footer public). */
export const LEGAL_LINKS: ReadonlyArray<{ to: string; label: string }> = [
  { to: '/legal/mentions-legales', label: 'Mentions légales' },
  { to: '/legal/cgu', label: 'Conditions générales' },
  { to: '/legal/privacy', label: 'Politique de confidentialité' },
];

interface LegalLayoutProps {
  title: string;
  description: string;
  children: ReactNode;
}

export function LegalLayout({ title, description, children }: LegalLayoutProps): JSX.Element {
  useEffect(() => {
    document.title = `${title} — ${PRODUCT.name}`;
    const descriptor = document.querySelector('meta[name="description"]');
    if (descriptor) descriptor.setAttribute('content', description);
  }, [title, description]);

  return (
    <main className="mx-auto max-w-3xl px-4 py-10">
      <nav className="mb-6 text-sm">
        <Link to="/" className="font-medium text-slate-600 hover:text-slate-900">
          ← Accueil
        </Link>
      </nav>

      <article>
        <h1 className="text-3xl font-bold text-slate-900">{title}</h1>
        <p className="mt-2 text-sm text-slate-500">Dernière mise à jour : {LEGAL_LAST_UPDATED}</p>
        <div className="mt-6 space-y-2">{children}</div>
      </article>

      <footer className="mt-12 border-t border-slate-200 pt-6">
        <ul className="flex flex-wrap gap-x-6 gap-y-2 text-sm">
          {LEGAL_LINKS.map((link) => (
            <li key={link.to}>
              <Link to={link.to} className="text-slate-600 underline hover:text-slate-900">
                {link.label}
              </Link>
            </li>
          ))}
        </ul>
      </footer>
    </main>
  );
}

/** Titre de section (h2) — homogénéise l'espacement entre pages légales. */
export function LegalSection({
  heading,
  children,
}: {
  heading: string;
  children: ReactNode;
}): JSX.Element {
  return (
    <section className="mt-8">
      <h2 className="text-xl font-semibold text-slate-900">{heading}</h2>
      <div className="mt-3 space-y-3 leading-relaxed text-slate-700">{children}</div>
    </section>
  );
}
